import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import './Toast.css';

/**
 * Lightweight in-app toast notifications — a replacement for native `alert()` popups.
 *
 * Usage:
 *   const { showToast } = useToast();
 *   showToast('Saved to My Ideas');              // success (default)
 *   showToast('Failed to save.', 'error');       // error
 *
 * The provider lives above the Router (see App.jsx), so toasts survive route changes —
 * e.g. a "Saved" toast fired right before navigating still shows on the destination page.
 */
const ToastContext = createContext(null);

export function useToast() {
  const ctx = useContext(ToastContext);
  // Fail soft if used outside a provider so a missing wrapper never crashes a page.
  return ctx || { showToast: () => {}, dismissToast: () => {} };
}

let nextId = 0;

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const timers = useRef({});

  const dismissToast = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
    if (timers.current[id]) {
      clearTimeout(timers.current[id]);
      delete timers.current[id];
    }
  }, []);

  const showToast = useCallback(
    (message, type = 'success', duration = 3500) => {
      const id = ++nextId;
      setToasts((prev) => [...prev, { id, message, type }]);
      timers.current[id] = setTimeout(() => dismissToast(id), duration);
      return id;
    },
    [dismissToast],
  );

  return (
    <ToastContext.Provider value={{ showToast, dismissToast }}>
      {children}
      <div className="toast-container" role="status" aria-live="polite">
        {toasts.map((t) => (
          <button
            key={t.id}
            type="button"
            className={`toast toast-${t.type}`}
            onClick={() => dismissToast(t.id)}
            aria-label="Dismiss notification"
          >
            <span className="toast-icon" aria-hidden="true">
              {t.type === 'error' ? '!' : '✓'}
            </span>
            <span className="toast-message">{t.message}</span>
          </button>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export default ToastProvider;
